import { observer } from 'mobx-react-lite';
import { DeliveryOptionLabels } from '../../../models/delivery-options/common';
import { DeliveryOption } from '../../../models/delivery-options/delivery-option';
import DeliveryOptionCardLeft from './card-left/delivery-option-card-left';
import DeliveryOptionCardLabel from './delivery-option-card-label.component';
import { DeliveryOptionRibbonLabelContainer } from './delivery-option-card-label.styles';
import {
  DeliveryOptionCardContainer,
  DeliveryOptionCardOuterWrapper,
} from './delivery-option-card.styles';
import {
  FirstMile,
  LastMile,
} from '../../../models/delivery-options/delivery-miles';
import { useStore } from '../../../stores/store';
import { useScrollToElement } from '../../../hooks/use-scroll-to-element';
import { useIsMobile } from '../../../hooks/use-is-mobile.hook';
import { prepareHint } from '../../../utils/elements-parser/elements-parser.utils';
import DeliveryOptionCardRight from './card-right/delivery-option-card-right';
import { testIds } from '../../../../test-ids';

interface Props {
  labels?: DeliveryOptionLabels[];
  deliveryOption: DeliveryOption;
  currencySymbol: string;
  onCardSelected?: (option: DeliveryOption) => void;
  onFirstMileSelected?: (
    option: DeliveryOption,
    lastMile: FirstMile,
    isOptionSelected: boolean
  ) => void;
  onLastMileSelected?: (
    option: DeliveryOption,
    lastMile: LastMile,
    isOptionSelected: boolean
  ) => void;
}

const DeliveryOptionCard = ({
  deliveryOption,
  labels,
  currencySymbol,
  onCardSelected,
  onFirstMileSelected,
  onLastMileSelected,
}: Props) => {
  const {
    parcelCreationStore: { selectedDeliveryOption },
  } = useStore();

  const isMobile = useIsMobile();

  const isOptionSelected =
    selectedDeliveryOption?.uiKey === deliveryOption.uiKey;

  const scrollRef = useScrollToElement<HTMLDivElement>(isOptionSelected);

  const { hint } = deliveryOption;

  const handleCardSelected = () => {
    if (isOptionSelected || !onCardSelected || !deliveryOption) return;

    onCardSelected(deliveryOption);
  };

  return (
    <DeliveryOptionCardOuterWrapper
      $selected={isOptionSelected}
      data-delivery-option-ui-key={deliveryOption.uiKey}
      data-testid={testIds.parceCreation.deliveryOptions.card}>
      <DeliveryOptionCardContainer
        ref={scrollRef}
        onClick={(e) => {
          e.stopPropagation();
          if (!isMobile) return;
          handleCardSelected();
        }}>
        <DeliveryOptionRibbonLabelContainer>
          {labels?.map((label, index) => (
            <DeliveryOptionCardLabel
              type={label}
              order={index}
              key={label}
            />
          ))}
        </DeliveryOptionRibbonLabelContainer>
        <DeliveryOptionCardLeft
          option={deliveryOption}
          isOptionSelected={isOptionSelected}
          onFirstMileSelected={onFirstMileSelected}
          onLastMileSelected={onLastMileSelected}
          currencySymbol={currencySymbol}
        />
        <DeliveryOptionCardRight
          option={deliveryOption}
          currencySymbol={currencySymbol}
          onCardSelected={handleCardSelected}
          isOptionSelected={isOptionSelected}
        />
      </DeliveryOptionCardContainer>
      {isOptionSelected && hint && hint.length > 0 && prepareHint(hint)}
    </DeliveryOptionCardOuterWrapper>
  );
};

export default observer(DeliveryOptionCard);
